import {
  boolean,
  index,
  integer,
  numeric,
  pgEnum,
  pgTable,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import { householdMembersTable } from "./householdMembersSchema";
import { customTimestamp } from "./utils/types";
import { workRequirementsTable } from "./workRequirementsSchema";

export const workRequirementExemptionEnumSchema = z.enum([
  "CAREGIVER_OF_CHILD_13_OR_YOUNGER",
  "CAREGIVER_OF_PERSON_WITH_DISABILITY",
  "PREGNANT_OR_POSTPARTUM",
  "MEDICALLY_FRAIL",
  "IN_DRUG_OR_ALCOHOL_TREATMENT",
  "MEETING_SNAP_TANF_WORK_REQUIREMENTS",
  "NOT_ENROLLED_OR_ELIGIBLE_FOR_MEDICARE",
  "FOSTER_YOUTH_UNDER_26",
  "TRIBAL_MEMBER",
  "VETERAN_100_PERCENT_DISABILITY",
  "INCARCERATED_OR_RELEASED_LAST_90_DAYS",
]);
export type WorkRequirementExemptionEnumType = z.infer<
  typeof workRequirementExemptionEnumSchema
>;

export const workRequirementMemberStatusEnumSchema = z.enum([
  "EXEMPT",
  "MEETS_REQUIREMENTS",
  "DOES_NOT_MEET_REQUIREMENTS",
]);
export type WorkRequirementMemberStatusEnumType = z.infer<
  typeof workRequirementMemberStatusEnumSchema
>;
export const workRequirementMemberStatusEnum = pgEnum(
  "work_requirement_member_status_enum",
  workRequirementMemberStatusEnumSchema.enum,
);

const hoursColumn = (name: string) =>
  numeric(name, { precision: 6, scale: 2, mode: "number" });

export const workRequirementHouseholdMembersTable = pgTable(
  "work_requirement_household_members",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    workRequirementId: uuid("work_requirement_id")
      .references(() => workRequirementsTable.id, { onDelete: "cascade" })
      .notNull(),
    householdMemberId: uuid("household_member_id")
      .references(() => householdMembersTable.id, { onDelete: "cascade" })
      .notNull(),
    // Null while the screening is still in progress.
    status: workRequirementMemberStatusEnum("status"),
    // Stored as text[] rather than an enum array so GCP Datastream can
    // replicate it to BigQuery. See issue #512.
    exemptions: text("exemptions")
      .array()
      .$type<WorkRequirementExemptionEnumType[]>()
      .notNull()
      .default([]),
    hasQualifyingIncome: boolean("has_qualifying_income"),
    employmentHours: hoursColumn("employment_hours"),
    employmentMonthlyIncome: integer("employment_monthly_income"),
    jobTrainingHours: hoursColumn("job_training_hours"),
    schoolHours: hoursColumn("school_hours"),
    communityServiceHours: hoursColumn("community_service_hours"),
    volunteerOfframpClickedAt: customTimestamp("volunteer_offramp_clicked_at"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    unique("work_requirement_household_members_unique_idx").on(
      table.workRequirementId,
      table.householdMemberId,
    ),
    index("work_requirement_household_members_household_member_id_index").on(
      table.householdMemberId,
    ),
  ],
);

const hoursSchema = z.number().min(0).max(744).nullish();

export const insertWorkRequirementHouseholdMemberSchema = createInsertSchema(
  workRequirementHouseholdMembersTable,
  {
    workRequirementId: z.uuid(),
    householdMemberId: z.uuid(),
    status: workRequirementMemberStatusEnumSchema.nullish(),
    exemptions: z.array(workRequirementExemptionEnumSchema),
    hasQualifyingIncome: z.boolean().nullish(),
    employmentHours: hoursSchema,
    employmentMonthlyIncome: z.number().int().min(0).nullish(),
    jobTrainingHours: hoursSchema,
    schoolHours: hoursSchema,
    communityServiceHours: hoursSchema,
    volunteerOfframpClickedAt: z.iso.datetime().nullish(),
  },
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertWorkRequirementHouseholdMemberType = z.infer<
  typeof insertWorkRequirementHouseholdMemberSchema
>;

export const workRequirementHouseholdMemberSchema = createSelectSchema(
  workRequirementHouseholdMembersTable,
  {
    status: workRequirementMemberStatusEnumSchema.nullable(),
    exemptions: z.array(workRequirementExemptionEnumSchema),
  },
);
export type WorkRequirementHouseholdMemberType = z.infer<
  typeof workRequirementHouseholdMemberSchema
>;

// The answers a patient gives while screening; `status` is always computed
// server-side from these and never accepted from the client.
export const workRequirementScreeningAnswersSchema =
  insertWorkRequirementHouseholdMemberSchema
    .pick({
      householdMemberId: true,
      exemptions: true,
      hasQualifyingIncome: true,
      employmentHours: true,
      employmentMonthlyIncome: true,
      jobTrainingHours: true,
      schoolHours: true,
      communityServiceHours: true,
    })
    .extend({
      exemptions: z.array(workRequirementExemptionEnumSchema).default([]),
    });
export type WorkRequirementScreeningAnswersType = z.infer<
  typeof workRequirementScreeningAnswersSchema
>;
